<template>
    <div class="welcome-page">
        <header class="page-header">
            <div class="page-header__inner">
                <p class="page-header__en">MAYORS CUP 2024</p>
                <h1>113年度 臺中市市長盃足球錦標賽</h1>
                <p class="subtitle">凝聚城市足球魂，打造臺中足球夢</p>
            </div>
        </header>

        <main class="main-content">
            <div class="group-tabs-container">
                <p class="group-tabs-label">選擇組別</p>
                <div class="group-tabs">
                    <button v-for="(tab, index) in tabs" :key="tab.id"
                        class="tab-button"
                        :class="{ active: activeTab === tab.id }"
                        @click="switchTab(tab.id)">
                        <i :class="tab.icon"></i>
                        <span>{{ tab.name }}</span>
                    </button>
                </div>
            </div>

            <div class="schedule-action">
                <router-link :to="'/mayors-cup/2024/' + activeTab" class="view-schedule">
                    <i class="fas fa-calendar-alt"></i>
                    查看完整賽程表
                </router-link>
            </div>

            <div class="results-container">
                <TransitionGroup name="list" tag="div" class="results-grid">
                    <div v-for="group in displayGroups" :key="group.id" class="result-card">
                        <div class="card-header">
                            <h3>{{ group.name }}</h3>
                            <div class="medal-icons">
                                <i class="fas fa-medal gold"></i>
                                <i class="fas fa-medal silver"></i>
                                <i class="fas fa-medal bronze"></i>
                            </div>
                        </div>
                        <div class="team-list">
                            <div v-for="(team, index) in group.teams" :key="index" class="team-item"
                                :class="getMedalClass(index)">
                                <span class="rank">{{ index + 1 }}</span>
                                <span class="name">{{ team }}</span>
                                <span v-if="index < 3" class="medal-label">{{ getMedalLabel(index) }}</span>
                            </div>
                        </div>
                    </div>
                </TransitionGroup>
            </div>
        </main>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

const tabs = [
    { id: 'school', name: '學校組', icon: 'fas fa-school' },
    { id: 'open', name: '公開組', icon: 'fas fa-trophy' }
]

const activeTab = ref('school')

const schoolGroups = [
    { id: 'school-junior', name: '國中組', teams: ['福科國中', '潭秀國中', '安和國中'] },
    { id: 'school-senior', name: '高年級組', teams: ['清水國小', '國安國小', '協和國小'] },
    { id: 'school-middle', name: '中年級組', teams: ['協和國小', '國安國小', '清水國小', '西屯國小'] }
]

const openGroups = [
    { id: 'open-high', name: '高中組', teams: ['足夢 U18A', '嶺東中學', 'FOCUS'] },
    { id: 'open-junior', name: '國中組', teams: ['福科', 'FUTURO U15', '潭秀 FC', '黎明'] },
    { id: 'open-senior-male', name: '高年級男生組', teams: ['足夢蜻蜓 A', 'FUTURO U12', '國安', '協和 FC'] },
    { id: 'open-senior-female', name: '高年級女生組', teams: ['南屯國小', '黎明國小', '惠來國小', '惠文獵豹'] },
    { id: 'open-middle-male', name: '中年級男生組', teams: ['旅人', '明道普霖斯頓飛翼', 'GAES 國安', '忠孝 U10'] },
    { id: 'open-middle-female', name: '中年級女生組', teams: ['南屯國小', '足夢蜻蜓', '臺中市永隆國小', '惠來國小'] },
    { id: 'open-low', name: '低年級組', teams: ['旅人 U8', '協和國小', '國安國小', '臺中市永隆國小'] },
    { id: 'open-kindergarten', name: '幼兒組', teams: ['威爾森教育機構', '小蜻蜓', '普霖斯頓'] }
]

const displayGroups = computed(() => activeTab.value === 'school' ? schoolGroups : openGroups)

const switchTab = (tabId: string) => { activeTab.value = tabId }

const getMedalClass = (index: number) => ['gold', 'silver', 'bronze'][index] || ''
const getMedalLabel = (index: number) => ['冠軍', '亞軍', '季軍'][index] || ''
</script>

<style lang="scss" scoped>
@use "sass:color";

.welcome-page {
    min-height: 100vh;
    background-color: #f7f9f7;
    opacity: 0;
    animation: fadeIn 0.5s ease-out forwards;
}

.page-header {
    background: url('@/assets/images/welcome-header.jpg') no-repeat center center / cover;
    position: relative;
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(#0a0a0a, 0.55);
    }

    &__inner {
        position: relative;
        z-index: 1;
        padding: 0 1.5rem;
    }

    &__en {
        font-size: 0.85rem;
        font-weight: 500;
        letter-spacing: 0.3em;
        color: #B89968;
        margin-bottom: 1.25rem;
        text-transform: uppercase;
    }

    h1 {
        font-size: 2.5rem;
        font-weight: 800;
        color: #ffffff;
        margin-bottom: 1rem;
        animation: slideIn 0.6s ease-out forwards;

        @media (max-width: $mobile-width) { font-size: 1.8rem; }
    }

    .subtitle {
        font-size: 1.1rem;
        color: rgba(#ffffff, 0.7);
        letter-spacing: 0.05em;
        animation: slideIn 0.6s ease-out 0.2s forwards;

        @media (max-width: $mobile-width) { font-size: 1rem; }
    }
}

.main-content {
    max-width: $container-width;
    margin: 0 auto;
    padding: 2rem 1.5rem;

    @media (max-width: $mobile-width) { padding: 1.5rem 1rem; }
}

.group-tabs-container {
    background: #f5f0e8;
    border-radius: 8px;
    border-top: 3px solid #B89968;
    padding: 1.25rem 1.5rem;
    margin-bottom: 2rem;
}

.group-tabs-label {
    font-size: 0.95rem;
    color: #3f4a52;
    margin-bottom: 1rem;
    font-family: 'Noto Serif TC', serif;
}

.group-tabs {
    display: flex;
    gap: 0.75rem;

    .tab-button {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.6rem 1.25rem;
        background: #ffffff;
        border: 1px solid rgba(#3f4a52, 0.15);
        border-radius: 6px;
        color: rgba(#3f4a52, 0.6);
        font-size: 0.95rem;
        cursor: pointer;
        transition: all 0.2s ease;

        &.active {
            border-color: #B89968;
            color: #B89968;
        }

        &:hover:not(.active) {
            border-color: rgba(#3f4a52, 0.3);
            color: #3f4a52;
        }

        i { font-size: 1rem; }
    }
}

.schedule-action {
    display: flex;
    justify-content: flex-end;
    margin: 2rem 0;

    .view-schedule {
        display: inline-flex;
        align-items: center;
        gap: 0.75rem;
        background: #B89968;
        color: $white-color;
        padding: 0.875rem 1.5rem;
        border-radius: 8px;
        text-decoration: none;
        font-weight: 500;
        transition: all 0.3s ease;

        &:hover { background: color.adjust(#B89968, $lightness: -5%); }

        i { font-size: 1.1rem; }

        @media (max-width: $mobile-width) {
            width: 100%;
            justify-content: center;
        }
    }
}

.results-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    padding-bottom: 2rem;

    @media (max-width: $mobile-width) { gap: 1rem; }
}

.list-move, .list-enter-active, .list-leave-active { transition: all 0.5s ease; }
.list-enter-from, .list-leave-to { opacity: 0; transform: translateX(30px); }
.list-leave-active { position: absolute; }

.result-card {
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid rgba(#3f4a52, 0.08);
    border-top: 3px solid #B89968;
    overflow: hidden;
    transition: transform 0.3s ease;

    &:hover { transform: translateY(-4px); }

    .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1.25rem;
        background: #f5f0e8;
        border-bottom: 1px solid rgba(#3f4a52, 0.08);

        h3 {
            margin: 0;
            color: #3f4a52;
            font-size: 1.1rem;
            font-weight: 700;
        }

        .medal-icons {
            display: flex;
            gap: 0.5rem;

            .fa-medal {
                font-size: 1.2rem;
                &.gold   { color: $accent-orange; }
                &.silver { color: $gray-200; }
                &.bronze { color: $second-color; }
            }
        }

        @media (max-width: $mobile-width) { padding: 1rem; }
    }

    .team-list {
        .team-item {
            display: flex;
            align-items: center;
            padding: 1rem 1.25rem;
            border-bottom: 1px solid rgba(#3f4a52, 0.06);

            &:last-child { border-bottom: none; }

            .rank  { width: 2rem; font-weight: 600; color: rgba(#3f4a52, 0.3); }
            .name  { flex: 1; color: rgba(#3f4a52, 0.8); }

            .medal-label {
                padding: 0.4rem 1rem;
                border-radius: 20px;
                font-size: 0.9rem;
                font-weight: 500;
                color: $white-color;
            }

            &.gold   .medal-label { background: linear-gradient(45deg, $accent-orange, color.adjust($accent-orange, $lightness: 10%)); }
            &.silver .medal-label { background: linear-gradient(45deg, $gray-200, $gray-100); }
            &.bronze .medal-label { background: linear-gradient(45deg, $second-color, color.adjust($second-color, $lightness: 10%)); }

            @media (max-width: $mobile-width) {
                padding: 0.875rem 1rem;
                .medal-label { padding: 0.3rem 0.75rem; font-size: 0.85rem; }
            }
        }
    }
}

@media (hover: none) {
    .result-card:hover { transform: none; }
    .tab-button:active { background: rgba($primary-color, 0.05); }
    .view-schedule:active { transform: scale(0.98); }
}

.tab-button:focus,
.view-schedule:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(#B89968, 0.3);
}

@keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
}

@keyframes slideIn {
    from { opacity: 0; transform: translateY(-10px); }
    to   { opacity: 1; transform: translateY(0); }
}
</style>
